import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Container } from '@material-ui/core';
import "./index.css"
import { useTranslation } from "react-i18next";
import ExitModalAndSave from '../Modal/ExitModalAndSave';
import { RouteComponentProps, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector, useStore } from 'react-redux';
import * as api from "../../api";
import { addHazardAssessmentAnswer, saveHazardGetQuestionsData, savePreAssessmentReturnQuestion, updateHazardAssessmentAnswer } from '../../ducks/redux/actions/hazardAssessment';
import classes from "../RepairUploadMedia/styles.module.css";
import styles from "../RepairUploadMedia/spinner.module.css";

interface HazardAssessmentProps extends RouteComponentProps<any> {}
interface HazardResponse {
  code: string;
  text: string;
  score: number;
}

interface HazardQuestion {
  code: string;
  label: string;
  category: string;
  sequence: number;
  type: string;
  options: HazardResponse[];
  dependsOn: { code: string; responses: string[] }[];
}

const getPreviousAnsweredSequence = (
  responses: any[],
  currentCode?: string,
  currentSequence?: number,
) => {
  if (!responses?.length || !currentSequence) {
    return null;
  }

  const sortedResponses = [...responses].sort(
    (a: any, b: any) => Number(a.sequence) - Number(b.sequence),
  );

  if (currentCode) {
    const currentResponseIndex = sortedResponses.findIndex(
      (item: any) => item.code === currentCode,
    );

    if (currentResponseIndex > 0) {
      return Number(sortedResponses[currentResponseIndex - 1].sequence);
    }
  }

  const previousSequence = sortedResponses
    .map((item: any) => Number(item.sequence))
    .filter((sequence: number) => Number.isFinite(sequence) && sequence < currentSequence)
    .sort((a: number, b: number) => b - a)[0];

  return previousSequence || null;
};

const areAnswerCodesEqual = (left: any[] = [], right: any[] = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const sortedLeft = [...left].map(String).sort((a, b) => a.localeCompare(b));
  const sortedRight = [...right].map(String).sort((a, b) => a.localeCompare(b));

  return sortedLeft.every((code, index) => code === sortedRight[index]);
};

export const isDependencyMatched = (
  question: any,
  responses: any[]
): boolean => {
  if (!question.dependsOn?.length) {
    return false;
  }

  return question.dependsOn.some((dependency: any) => {
    const parentResponse = responses.find(
      (resp: any) => resp.code === dependency.code
    );

    if (!parentResponse?.answerCodes?.length) {
      return false;
    }

    return dependency.responses.some((expectedCode: string) =>
      parentResponse.answerCodes.includes(expectedCode)
    );
  });
};

export const getNextQuestionIndex = (
  questions: any[],
  currentQuestion: any,
  updatedResponses: any[]
) => {
  const currentIndex = questions.findIndex(
    (q) => q.code === currentQuestion.code
  );

  const nextIndex = currentIndex + 1;
  const nextQuestion = questions[nextIndex];

  if (!nextQuestion) {
    return -1;
  }

  if (
    !nextQuestion.dependsOn?.length ||
    isDependencyMatched(nextQuestion, updatedResponses)
  ) {
    return nextIndex;
  }

  const matchingDependentIndex = questions.findIndex(
    (question, index) =>
      index > nextIndex &&
      question.dependsOn?.length &&
      isDependencyMatched(question, updatedResponses),
  ); 

  return matchingDependentIndex !== -1 ? matchingDependentIndex : nextIndex;
};

const HazardAssessment: React.FC<HazardAssessmentProps> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useStore<any>();
  const selectedRepairReasonObj = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);
  const assetDetails = useSelector((state: any) => state.dashboardReducer?.formState?.assetByIdDataObject);
  const savedQuestions = useSelector((state: any) => state.hazard?.hazardQuestions);
  const hazardResponses = useSelector((state: any) => state.hazard.responses);
  const [questions, setQuestions] = useState<HazardQuestion[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const { seq } = useParams<{ seq: string }>();
  const [error, setError] = useState<string>(""); 
  const [currentIndex, setCurrentIndex] = useState(Number(seq) - 1 || 0);
  const currentQuestion = questions?.[currentIndex];
  const [isLoading, setIsLoading] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const continuingRef = useRef(false);
  const workOrderObject = useSelector((state: any) => state?.addresses?.workOrderRepair);

  useEffect(() => {
    continuingRef.current = false;
    setIsContinuing(false);
  }, [currentQuestion]);

  const getHHSRSHazardQuestions = useCallback(async () => {
    try {
      const assetType = assetDetails?.assetType?.toUpperCase();
      const hazardType = selectedRepairReasonObj?.[0]?.hazardType;

      if (savedQuestions?.length) {
        setQuestions(savedQuestions);
        setIsLoading(false);
        return;
      }

      if (!assetType || !hazardType) {
        setIsLoading(false);
        return;
      }

      const res = await api.getHHSRSHazardQuestions(assetType, hazardType);
      const questions = res?.[0]?.questions;

      setQuestions(questions);
      setShowAlert(selectedRepairReasonObj?.[0]?.isHazard === true && questions[currentIndex]?.sequence === 1);

      if (!savedQuestions?.length && questions?.length) {
        dispatch(saveHazardGetQuestionsData(questions));
      }
       setIsLoading(false);
    } catch (e) {
      console.error(e);
      setIsLoading(false);
    }
  }, [
    assetDetails?.assetType,
    selectedRepairReasonObj?.[0]?.hazardType,
    savedQuestions,
    dispatch,
  ]);

  useEffect(() => {
    if (!currentQuestion) return;
    const existingAnswer = hazardResponses?.find(
      (item: any) => item.code === currentQuestion.code
    );

    if (existingAnswer) {
      setSelectedAnswers([...existingAnswer.answerCodes]); // spread to avoid mutating Redux state
    } else {
      setSelectedAnswers([]);
    }
  }, [currentQuestion, hazardResponses]);

  useEffect(() => {
    setIsLoading(true);
    getHHSRSHazardQuestions();

  }, []);

  useEffect(() => {
    if (
      selectedRepairReasonObj?.[0]?.isHazard === false &&
      selectedRepairReasonObj?.[0]?.isPreAssessmentRequired !== true
    ) {
      props.history.push("/RepairUploadmedia");
    }
  }, []);
  useEffect(() => {
  if (questions?.length && seq) {
    setCurrentIndex(Number(seq) - 1);
  }
    setShowAlert(selectedRepairReasonObj?.[0]?.isHazard === true && questions?.[currentIndex]?.sequence === 1);

}, [seq, questions]);


  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
    if (e.target.checked) {
      setSelectedAnswers((prev) => [...prev, code]);
    } else {
      setSelectedAnswers((prev) => prev.filter((c) => c !== code));
    }
    setError("");
  };

    const handleContinue = () => {
    if (continuingRef.current || isLoading || !currentQuestion) return;
    if (selectedAnswers.length === 0) {
      setError(t("Hazard_Select_Answer_Error"));
      return;
    }

    setError("");
    continuingRef.current = true;
    setIsContinuing(true);

    const selectedResponseTexts = currentQuestion.options
      .filter((response: any) => selectedAnswers.includes(response.code))
      .map((response: any) => response.text);

    const payload = {
      sequence: currentQuestion.sequence,
      question: currentQuestion.label,
      answers: selectedResponseTexts,
      answerCodes: [...selectedAnswers], // spread to avoid mutation
      code: currentQuestion.code,
    };

    const existingQuestion = hazardResponses.some(
      (item: any) => item.code === currentQuestion.code
    );

    const existingSavedAnswer = hazardResponses.find(
      (item: any) => item.code === currentQuestion.code,
    );

    const hasAnswerChanged = !areAnswerCodesEqual(
      existingSavedAnswer?.answerCodes || [],
      selectedAnswers,
    );

    if (existingQuestion && hasAnswerChanged) {
      dispatch(updateHazardAssessmentAnswer(payload));
    } else if (!existingQuestion) {
      dispatch(addHazardAssessmentAnswer(payload));
    }

    const updatedResponses = existingQuestion
      ? hazardResponses.map((item: any) =>
          item.code === currentQuestion.code && hasAnswerChanged ? payload : item
        )
      : [...hazardResponses, payload];

    const nextIndex = getNextQuestionIndex(questions, currentQuestion,
      existingQuestion && hasAnswerChanged ? store.getState().hazard.responses : updatedResponses);

    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
      setSelectedAnswers([]);
      setShowAlert(false);
      history.push(`/HazardAssessment/${nextIndex+1}`);
    } else {
      history.push("/HazardReview");
    }
  };

  const backButtonHandler = () => {
    if (continuingRef.current) return;
    setError("");
    const previousSequence =  getPreviousAnsweredSequence(
      hazardResponses.filter((response: any) => !response.isPreAssessment && response.answerCodes?.length && questions.some((question) => question.code === response.code)),
      currentQuestion?.code,
      currentQuestion?.sequence,
    );

    const currentSequence = Number(currentQuestion?.sequence);

    const gap =
      Number.isFinite(currentSequence) && Number.isFinite(Number(previousSequence))
        ? currentSequence - Number(previousSequence)
        : 0;

    if (gap > 0 && previousSequence !== null) {

  const previousIndexByGap = questions.findIndex((question) => Number(question.sequence) === previousSequence);
  const prevPage = previousIndexByGap + 1;

  setCurrentIndex(previousIndexByGap);

  history.push(`/HazardAssessment/${prevPage}`);
} else if (selectedRepairReasonObj?.[0]?.isPreAssessmentRequired === true) {
      const lastPreAssessmentResponse = [...hazardResponses]
        .filter((response: any) => response.isPreAssessment && response.answerCodes?.length)
        .sort((first: any, second: any) => Number(second.sequence) - Number(first.sequence))?.[0];

      dispatch(savePreAssessmentReturnQuestion({
        code: lastPreAssessmentResponse?.code || "",
        from: "HazardAssessment",
      }));
      props.history.push("/HazardPreassessment");
    } else {
      props.history.push("/RepairAdditionalDetails");
    }
  };

  const exitButtonHandler = () => {
    history.push("/LandingView");
  };

  const saveDraftAndExit = () => history.push(`/WorkOrder360View/${workOrderObject.id}`);

  const renderCurrentQuestions = () => {
    if (!currentQuestion) return null;

    return (
      <div className="relative pb-2 bg-white container mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
        <h2 className="text-purple-100 text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
          {currentQuestion.label}
        </h2>

        {/* DROPDOWN */}
        {currentQuestion.type === "DROPDOWN" && (
          <select
            aria-label={currentQuestion.code}
            value={selectedAnswers[0] || ""}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedAnswers(val ? [val] : []);
              if (val) setError("");
            }}
            className="block dropDownWidth rounded border border-grey mb-4 bg-grey"
          >
            <option value="">Select an option</option>
            {currentQuestion.options?.map((option: any) => (
              <option key={option.code} value={option.code}>
                {option.text}
              </option>
            ))}
          </select>
        )}



        {/* CHECKBOX */}
        {currentQuestion.type === "CHECKBOX" && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth">
            {currentQuestion.options?.map((option: any) => {
              const isChecked = selectedAnswers.includes(option.code);
              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: isChecked ? "1px solid #0B8B84" : "1px solid #d1d5db",
                    backgroundColor: isChecked ? "#E6F6F5" : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    minHeight: "48px",
                  }}
                >
                  <fieldset className="p-0 m-0 border-0">
                    <legend className="sr-only">{currentQuestion.label}</legend>
                    <input
                      type="checkbox"
                      id={`${currentQuestion.code}-${option.code}`}
                      name={currentQuestion.code}
                      value={option.code}
                      className="checkPos flex-shrink-0"
                      style={{ width: "18px", height: "18px", accentColor: "#0B8B84" }}
                      checked={isChecked}
                      onChange={(e) => handleCheckboxChange(e, option.code)}
                      aria-label={option.text}
                    />
                  </fieldset>
                  <span className="ml-3 text-sm w-90 inline-block">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* RADIO */}
        {currentQuestion.type === "RADIO" && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth">
            {currentQuestion.options?.map((option: any) => {
              const isChecked = selectedAnswers.includes(option.code);
              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: isChecked ? "1px solid #0B8B84" : "1px solid #d1d5db",
                    backgroundColor: isChecked ? "#E6F6F5" : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    minHeight: "48px",
                  }}
                >
                  <input
                    type="radio"
                    id={`${currentQuestion.code}-${option.code}`}
                    name={currentQuestion.code}
                    value={option.code}
                    className="checkPos flex-shrink-0"
                    style={{ width: "18px", height: "18px", accentColor: "#0B8B84" }}
                    checked={isChecked}
                    onChange={() => { setSelectedAnswers([option.code]); setError(""); }}
                    aria-label={option.text}
                  />
                  <span className="ml-3 text-sm w-90 inline-block">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Error message */}
       {(error !== "") && (
          <div
            data-testid="alert-warning"
            className="error mb-8 mt-4"
            role="alert"
            placeholder="alert-message"
          >
            <div className="w-full px-2 py-2 bg-yellow flex items-center">
              <div className="px-4 pr-8">
                <i className="fas fa-exclamation-triangle text-brick-70 w-6 h-6"></i>
              </div>
              <div className="flex items-center">
                <p
                  data-testid="locatioAlert_0"
                  className="w-full text-left my-2 font-AvantGardeGothic-Md"
                >
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Container id="preAssessment" role="main" className="ctn-fht">
     {isLoading ? (
        <div className={classes.overlay}>
          <div id="UploadingOverlay" data-testid="UpLoadingOverlay">
            <div className={styles.overlayContainer}>
              <div className={styles.overlayContent}>
                <div className={styles.spinner}></div>
              </div>
            </div>
          </div>
        </div>
      ) :
      (<><div className="container mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
        <div className="back-and-journey">
          <p id="back-button-presassement" className="text-teal text-xl mt-4 mb-4">
            <button
              className="back-button_hazard"
              data-testid="backButtonHandler"
              onClick={backButtonHandler}
              type="submit"
            >
              <i className="fas fa-angle-left mr-2"></i>
              {t("back")}
            </button>
          </p>
        </div>
      </div>
       {/* ==== Display purple alert warning === */}
      {showAlert && (
        <div
          data-testid="alert-warning"
          className="error mb-6 px-4 mt-2"
          role="alert"
          placeholder="alert-message"
        >
          <div className="w-full bg-purple-20 flex items-center container">
            <div className="px-4 pr-8">
              <i className="fa fa-bullhorn text-purple-100 w-6 h-6 purple-icon"></i>
            </div>
            <div className="flex items-center text-black">
              <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
                {t("HHSRS_Assessment_Alert")}
              </p>
            </div>
          </div>
        </div>
      )}
      {/* === Close purple warning === */}

      <div className="px-4 sm:px-0 mb-4">
        {questions && renderCurrentQuestions()}
      </div>

      <div
        className="flex justify-between items-center pb-4 md:pb-8 container sm:px-0"
        id="assessment-main-btn"
      >
        <ExitModalAndSave
          id="repreason-exitmodal"
          exit={exitButtonHandler}
          saveDraftAndExit={saveDraftAndExit}
        />
        <div className="col-md-12 marginTop-auto mt-4 mb-4">
          <div className="row m-0 footer-actionbtn">
            <div className="col text-end padding-repair">
              <button
                className="button-class"
                id="btn_hhrs_continue"
                onClick={handleContinue}
                disabled={isLoading || isContinuing || !currentQuestion || error !== ""}
                aria-busy={isContinuing}
                type="submit"
              >
                {isContinuing && <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true" />}
                {t("continue")}
                <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* End button */}
      </>
  )}
    </Container>
  );
};

export { HazardAssessment };
